import { createSlice } from '@reduxjs/toolkit';
import {
  fetchLiftTypesEnum,
  fetchManufacturers,
  fetchManufacturerControllers,
  fetchManufacturersWithControllers,
  fetchCombinedCompletion,
  fetchDefaultBore,
  fetchValveManufacturers,
  createCombinedCompletion,
  editCombinedCompletion,
  fetchPumpManufacturers,
  fetchPumpManufacturerSeries,
  fetchPumpManufacturerTypes,
  validateValvesCompletion,
} from '../../../services/well-completions/WellCompletionsService';

const initialState = {
  combinedCompletions: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  liftTypes: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  manufacturers: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  manufactureControllers: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  manufacturersWithControllers: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  defaultBore: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  valveManufacturers: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  pumpManufacturers: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  pump1ManufacturerSeries: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  pump1ManufacturerTypes: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  pump2ManufacturerSeries: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  pump2ManufacturerTypes: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  pump3ManufacturerSeries: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  pump3ManufacturerTypes: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  createCompletion: {
    data: undefined,
    loading: false,
    error: undefined as string | undefined,
  },
  editCompletion: {
    data: undefined,
    loading: false,
    error: undefined as string | undefined,
  },
  validateValves: {
    data: undefined,
    loading: false,
    error: undefined as string | undefined,
  },
  selectedLiftType: '2',
};

const CreateCompletionsSlice = createSlice({
  name: 'createCompletions',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.combinedCompletions = { data: undefined, error: undefined, loading: false };
      state.liftTypes = { data: undefined, error: undefined, loading: false };
      state.manufacturers = { data: undefined, error: undefined, loading: false };
      state.manufactureControllers = { data: undefined, error: undefined, loading: false };
      state.manufacturersWithControllers = { data: undefined, error: undefined, loading: false };
      state.defaultBore = { data: undefined, error: undefined, loading: false };
      state.valveManufacturers = { data: undefined, error: undefined, loading: false };
      state.createCompletion = { data: undefined, error: undefined, loading: false };
      state.pump1ManufacturerSeries = { data: undefined, error: undefined, loading: false };
      state.pump1ManufacturerTypes = { data: undefined, error: undefined, loading: false };
      state.pump2ManufacturerSeries = { data: undefined, error: undefined, loading: false };
      state.pump2ManufacturerTypes = { data: undefined, error: undefined, loading: false };
      state.pump3ManufacturerSeries = { data: undefined, error: undefined, loading: false };
      state.pump3ManufacturerTypes = { data: undefined, error: undefined, loading: false };
    },
    setSelectedLiftType: (state, action) => {
      state.selectedLiftType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      //Combined Completions
      .addCase(fetchCombinedCompletion.pending, (state) => {
        state.combinedCompletions.loading = true;
      })
      .addCase(fetchCombinedCompletion.fulfilled, (state, action) => {
        state.combinedCompletions.loading = false;
        state.combinedCompletions.data = action.payload;
      })
      .addCase(fetchCombinedCompletion.rejected, (state) => {
        state.combinedCompletions.loading = false;
      })
      // Lift Types
      .addCase(fetchLiftTypesEnum.pending, (state) => {
        state.liftTypes.loading = true;
      })
      .addCase(fetchLiftTypesEnum.fulfilled, (state, action) => {
        state.liftTypes.loading = false;
        state.liftTypes.data = action.payload;
      })
      .addCase(fetchLiftTypesEnum.rejected, (state) => {
        state.liftTypes.loading = false;
      })
      // Manufacturers
      .addCase(fetchManufacturers.pending, (state) => {
        state.manufacturers.loading = true;
      })
      .addCase(fetchManufacturers.fulfilled, (state, action) => {
        state.manufacturers.loading = false;
        state.manufacturers.data = action.payload;
      })
      .addCase(fetchManufacturers.rejected, (state) => {
        state.manufacturers.loading = false;
      })
      // manufactureControllers
      .addCase(fetchManufacturerControllers.pending, (state) => {
        state.manufactureControllers.loading = true;
      })
      .addCase(fetchManufacturerControllers.fulfilled, (state, action) => {
        state.manufactureControllers.loading = false;
        state.manufactureControllers.data = action.payload;
      })
      .addCase(fetchManufacturerControllers.rejected, (state) => {
        state.manufactureControllers.loading = false;
      })
      // manufacturersWithControllers
      .addCase(fetchManufacturersWithControllers.pending, (state) => {
        state.manufacturersWithControllers.loading = true;
      })
      .addCase(fetchManufacturersWithControllers.fulfilled, (state, action) => {
        state.manufacturersWithControllers.loading = false;
        state.manufacturersWithControllers.data = action.payload;
      })
      .addCase(fetchManufacturersWithControllers.rejected, (state) => {
        state.manufacturersWithControllers.loading = false;
      })
      // Default Bore
      .addCase(fetchDefaultBore.pending, (state) => {
        state.defaultBore.loading = true;
      })
      .addCase(fetchDefaultBore.fulfilled, (state, action) => {
        state.defaultBore.loading = false;
        state.defaultBore.data = action.payload;
      })
      .addCase(fetchDefaultBore.rejected, (state) => {
        state.defaultBore.loading = false;
      })
      // Valve Manufacturers
      .addCase(fetchValveManufacturers.pending, (state) => {
        state.valveManufacturers.loading = true;
      })
      .addCase(fetchValveManufacturers.fulfilled, (state, action) => {
        state.valveManufacturers.loading = false;
        state.valveManufacturers.data = action.payload;
      })
      .addCase(fetchValveManufacturers.rejected, (state) => {
        state.valveManufacturers.loading = false;
      })
      // Create Combined Completion
      .addCase(createCombinedCompletion.pending, (state) => {
        state.createCompletion.loading = true;
        state.createCompletion.error = undefined;
      })
      .addCase(createCombinedCompletion.fulfilled, (state, action) => {
        state.createCompletion.loading = false;
        state.createCompletion.data = action.payload;
      })
      .addCase(createCombinedCompletion.rejected, (state, action) => {
        state.createCompletion.loading = false;
        state.createCompletion.error = action.error.message || 'Failed to create completion';
      })
      // Edit Combined Completion
      .addCase(editCombinedCompletion.pending, (state) => {
        state.editCompletion.loading = true;
        state.editCompletion.error = undefined;
      })
      .addCase(editCombinedCompletion.fulfilled, (state, action) => {
        state.editCompletion.loading = false;
        state.editCompletion.data = action.payload;
      })
      .addCase(editCombinedCompletion.rejected, (state, action) => {
        state.editCompletion.loading = false;
        state.editCompletion.error = action.error.message || 'Failed to edit completion';
      })
      // fetch pump manufactures
      .addCase(fetchPumpManufacturers.pending, (state) => {
        state.pumpManufacturers.loading = true;
      })
      .addCase(fetchPumpManufacturers.fulfilled, (state, action) => {
        state.pumpManufacturers.loading = false;
        state.pumpManufacturers.data = action.payload;
      })
      .addCase(fetchPumpManufacturers.rejected, (state) => {
        state.pumpManufacturers.loading = false;
      })
      // pumps manufacturer series
      .addCase(fetchPumpManufacturerSeries.pending, (state, action) => {
        const field = action.meta?.arg?.field;
        if (field === 'pumpUpperManufacturer') {
          state.pump1ManufacturerSeries.loading = true;
          state.pump1ManufacturerSeries.data = undefined;
          state.pump1ManufacturerTypes.data = undefined;
        }
        if (field === 'pumpMidManufacturer') {
          state.pump2ManufacturerSeries.loading = true;
          state.pump2ManufacturerSeries.data = undefined;
          state.pump2ManufacturerTypes.data = undefined;
        }
        if (field === 'pumpLowerManufacturer') {
          state.pump3ManufacturerSeries.loading = true;
          state.pump3ManufacturerSeries.data = undefined;
          state.pump3ManufacturerTypes.data = undefined;
        }
      })
      .addCase(fetchPumpManufacturerSeries.fulfilled, (state, action) => {
        const field = action.meta?.arg?.field;
        if (field === 'pumpUpperManufacturer') {
          state.pump1ManufacturerSeries.data = action.payload;
          state.pump1ManufacturerSeries.loading = false;
        }
        if (field === 'pumpMidManufacturer') {
          state.pump2ManufacturerSeries.data = action.payload;
          state.pump2ManufacturerSeries.loading = false;
        }
        if (field === 'pumpLowerManufacturer') {
          state.pump3ManufacturerSeries.data = action.payload;
          state.pump3ManufacturerSeries.loading = false;
        }
      })
      .addCase(fetchPumpManufacturerSeries.rejected, (state, action) => {
        const field = action.meta?.arg?.field;
        if (field === 'pumpUpperManufacturer') {
          state.pump1ManufacturerSeries.loading = false;
        }
        if (field === 'pumpMidManufacturer') {
          state.pump2ManufacturerSeries.loading = false;
        }
        if (field === 'pumpLowerManufacturer') {
          state.pump3ManufacturerSeries.loading = false;
        }
      })
      // pumps manufacturer types
      .addCase(fetchPumpManufacturerTypes.pending, (state, action) => {
        const field = action.meta?.arg?.field;
        if (field === 'pumpUpperSeries') {
          state.pump1ManufacturerTypes.loading = true;
          state.pump1ManufacturerTypes.data = undefined;
        }
        if (field === 'pumpMidSeries') {
          state.pump2ManufacturerTypes.loading = true;
          state.pump2ManufacturerTypes.data = undefined;
        }
        if (field === 'pumpLowerSeries') {
          state.pump3ManufacturerTypes.loading = true;
          state.pump3ManufacturerTypes.data = undefined;
        }
      })
      .addCase(fetchPumpManufacturerTypes.fulfilled, (state, action) => {
        const field = action.meta?.arg?.field;
        if (field === 'pumpUpperSeries') {
          state.pump1ManufacturerTypes.loading = false;
          state.pump1ManufacturerTypes.data = action.payload;
        }
        if (field === 'pumpMidSeries') {
          state.pump2ManufacturerTypes.data = action.payload;
          state.pump2ManufacturerTypes.loading = false;
        }
        if (field === 'pumpLowerSeries') {
          state.pump3ManufacturerTypes.data = action.payload;
          state.pump3ManufacturerTypes.loading = false;
        }
      })
      .addCase(fetchPumpManufacturerTypes.rejected, (state, action) => {
        const field = action.meta?.arg?.field;
        if (field === 'pumpUpperSeries') {
          state.pump1ManufacturerTypes.loading = false;
        }
        if (field === 'pumpMidSeries') {
          state.pump2ManufacturerTypes.loading = false;
        }
        if (field === 'pumpLowerSeries') {
          state.pump3ManufacturerTypes.loading = false;
        }
      })
      // Validate valves
      .addCase(validateValvesCompletion.pending, (state) => {
        state.validateValves.loading = true;
        state.validateValves.error = undefined;
      })
      .addCase(validateValvesCompletion.fulfilled, (state, action) => {
        state.validateValves.loading = false;
        state.validateValves.data = action.payload;
      })
      .addCase(validateValvesCompletion.rejected, (state, action) => {
        state.validateValves.loading = false;
        state.validateValves.error = action.error.message || 'Failed to validate valves';
      });
  },
});

export const { setInitialState, setSelectedLiftType } = CreateCompletionsSlice.actions;
export default CreateCompletionsSlice.reducer;
